import { useMemo } from "react";
import { 
    generateGradientMetaMap, GREEN_GRADIENT, BLUE_GRADIENT, 
    ORANGE_GRADIENT, PURPLE_GRADIENT, YELLOW_GRADIENT, CYAN_GRADIENT, RED_GRADIENT
} from "../../../utils/cameraUtils";

// Градиент для клиентов без внимания (Янтарный / Предупреждающий)
const AMBER_GRADIENT = [
    "#ffe082", "#ffd54f", "#ffca28", "#ffc107", 
    "#ffb300", "#ffa000", "#ff8f00", "#ff6f00", 
    "#e65100", "#bf360c", "#000000"
];

export const useCameraAnalytics = (analytics, camera, t, minPercentage) => {
  
  // 1. Общее присутствие (Зеленый)
  const personnelGeneralMeta = useMemo(() => 
    generateGradientMetaMap(-100, -1, GREEN_GRADIENT, "Сотрудники", "Нет сотрудников"), 
  [t]);
  
  // 2. Сидят на стуле (Синий)
  const chairMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-300, -30, BLUE_GRADIENT, "Сидит", "Не сидят"); 
    meta["-31"] = { label: "Сидит", color: "#3399ff" }; 
    return meta; 
  }, [t]);
  
  // 3. В телефоне (Оранжевый)
  const phoneMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-400, -40, ORANGE_GRADIENT, "В телефоне", "Без телефона"); 
    meta["-41"] = { label: "В телефоне", color: "#ff9933" }; 
    return meta; 
  }, [t]);

  // 4. Работа с цветами (Фиолетовый)
  const workFlowersMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-800, -80, PURPLE_GRADIENT, "Работают с цветами", "Не работают с цветами"); 
    meta["-81"] = { label: "Работают с цветами", color: "#9c27b0" }; 
    return meta; 
  }, [t]);

  // 5. Без формы (Желтый)
  const noUniformMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-900, -90, YELLOW_GRADIENT, "Без формы", "В форме"); 
    meta["-91"] = { label: "Без формы", color: "#ffb300" }; 
    return meta; 
  }, [t]);

  // 6. Холодильник - Пустые места (Голубой/Бирюзовый)
  const fridgeMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-1000, -100, CYAN_GRADIENT, "Пустые места", "Все заполнено"); 
    meta["-1001"] = { label: "Пустые места", color: "#00bcd4" }; 
    return meta; 
  }, [t]);

  // 7. Контроль чека (Красный)
  const receiptMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-1100, -110, RED_GRADIENT, "Чек/Касса", "Нет чека"); 
    meta["-1101"] = { label: "Чек/Касса обнаружена", color: "#f44336" }; 
    return meta; 
  }, [t]);

  // 8. НОВОЕ: Клиенты без внимания в холодильнике (>1 мин) (Янтарный)
  const unattendedClientMeta = useMemo(() => { 
    const meta = generateGradientMetaMap(-1200, -120, AMBER_GRADIENT, "Клиент без сотрудника", "Сотрудник на месте / Нет клиентов"); 
    meta["-1201"] = { label: "Клиент без сотрудника (>1 мин)", color: "#ff9800" }; 
    return meta; 
  }, [t]);

  // Динамическая статистика для боковой панели
  const dynamicSummary = useMemo(() => {
    if (!analytics) return null;

    // Считает сумму человеко-секунд
    const sumManSeconds = (list) => list?.reduce((acc, item) => acc + item.people_count * item.total_duration_seconds, 0) || 0;
    
    const totalDowntime = analytics.general?.total_downtime_seconds || 0;
    
    // Считаем время по всем метрикам
    const totalChair = sumManSeconds(analytics.chair?.durations_by_people_count);
    const totalPhone = sumManSeconds(analytics.phone?.durations_by_people_count);
    const totalWorkFlowers = sumManSeconds(analytics.work_flowers?.durations_by_people_count);
    const totalNoUniform = sumManSeconds(analytics.no_uniform?.durations_by_people_count);
    const totalFridgeEmpty = sumManSeconds(analytics.fridge_empty?.durations_by_people_count);
    const totalReceipts = sumManSeconds(analytics.receipts?.durations_by_people_count);
    
    // НОВОЕ: Время ожидания клиентов без сотрудников и список инцидентов
    const totalUnattended = sumManSeconds(analytics.unattended_clients?.durations_by_people_count);
    const unattendedIncidents = analytics.unattended_incidents || [];

    let totalWorkManSeconds = 0;
    let filteredWorkDuration = 0;
    let totalWorkDuration = 0;
    let maxPeople = 0;

    const durationsList = analytics.general?.durations_by_people_count || [];
    totalWorkDuration = durationsList.reduce((acc, item) => acc + item.total_duration_seconds, 0);

    // Фильтруем всплески (только то, что > minPercentage)
    if (totalWorkDuration > 0) {
      [...durationsList].sort((a, b) => b.people_count - a.people_count).forEach((item) => {
        if ((item.total_duration_seconds / totalWorkDuration) * 100 >= minPercentage) {
          totalWorkManSeconds += item.people_count * item.total_duration_seconds;
          filteredWorkDuration += item.total_duration_seconds;
          if (maxPeople === 0) maxPeople = item.people_count;
        }
      });
    }

    // КПД активности
    const efficiency = totalWorkManSeconds > 0 ? (Math.max(0, totalWorkManSeconds - (totalChair + totalPhone)) / totalWorkManSeconds) * 100 : 0;
    const presenceEfficiency = (totalWorkManSeconds + totalDowntime) > 0 ? (totalWorkManSeconds / (totalWorkManSeconds + totalDowntime)) * 100 : 0;
    const flowersEfficiency = totalWorkManSeconds > 0 ? (totalWorkFlowers / totalWorkManSeconds) * 100 : 0;
    const weightedAveragePeople = filteredWorkDuration > 0 ? totalWorkManSeconds / filteredWorkDuration : 0;

    return { 
        maxPeople, 
        weightedAveragePeople, 
        totalDowntime, 
        totalWork: totalWorkManSeconds, 
        totalChair, 
        totalPhone, 
        totalWorkFlowers,
        totalNoUniform,
        totalFridgeEmpty,
        totalReceipts,
        totalUnattended,
        unattendedIncidents,
        efficiency, 
        presenceEfficiency,
        flowersEfficiency
    };
  }, [analytics, minPercentage]);

  return {
    personnelGeneralMeta, 
    chairMeta, 
    phoneMeta, 
    workFlowersMeta, 
    noUniformMeta,
    fridgeMeta,
    receiptMeta,
    unattendedClientMeta, // Экспортируем новую мету
    dynamicSummary
  };
};